import { useCallback, useEffect, useRef } from "react"
import { useRecordMediaProgress, useViewProgress } from "@/entities/view-history"

interface UseVideoPlaybackProgressOptions {
	postId?: string
	selectedVideoId: string | null
	videoRef: React.RefObject<HTMLVideoElement | null>
}

export function useVideoPlaybackProgress({
	postId,
	selectedVideoId,
	videoRef,
}: UseVideoPlaybackProgressOptions) {
	const recordMediaProgress = useRecordMediaProgress()
	const restoredProgressForMediaRef = useRef<Set<string>>(new Set())
	const lastSavedTimeRef = useRef<number>(0)

	const { data: viewProgress } = useViewProgress(
		postId || "",
		postId && selectedVideoId ? selectedVideoId : undefined,
	)

	const saveProgress = useCallback(() => {
		if (!videoRef.current || !postId || !selectedVideoId) return

		const currentTime = videoRef.current.currentTime
		const duration = videoRef.current.duration

		if (Math.abs(currentTime - lastSavedTimeRef.current) < 2) return

		lastSavedTimeRef.current = currentTime
		recordMediaProgress.mutate({
			postId,
			mediaId: selectedVideoId,
			videoProgress: currentTime,
			videoDuration: duration,
		})
	}, [postId, recordMediaProgress, selectedVideoId, videoRef])

	useEffect(() => {
		if (!selectedVideoId) return
		restoredProgressForMediaRef.current.delete(selectedVideoId)
		lastSavedTimeRef.current = 0
	}, [selectedVideoId])

	useEffect(() => {
		if (!postId || !selectedVideoId) return
		if (!viewProgress?.videoProgress) return
		if (!videoRef.current) return
		if (restoredProgressForMediaRef.current.has(selectedVideoId)) return

		try {
			videoRef.current.currentTime = viewProgress.videoProgress
			restoredProgressForMediaRef.current.add(selectedVideoId)
		} catch {}
	}, [postId, selectedVideoId, viewProgress?.videoProgress, videoRef])

	useEffect(() => {
		if (!postId) return

		const interval = setInterval(() => {
			if (videoRef.current && !videoRef.current.paused) {
				saveProgress()
			}
		}, 5000)

		return () => clearInterval(interval)
	}, [postId, saveProgress, videoRef])

	useEffect(() => {
		return () => {
			saveProgress()
		}
	}, [saveProgress])

	const prepareVideoSwitch = useCallback((mediaId: string) => {
		restoredProgressForMediaRef.current.delete(mediaId)
		lastSavedTimeRef.current = 0
	}, [])

	const tryRestoreOnMetadata = useCallback(
		(mediaId: string, video: HTMLVideoElement) => {
			if (!postId || !selectedVideoId) return
			if (mediaId !== selectedVideoId) return
			if (!viewProgress?.videoProgress) return
			if (restoredProgressForMediaRef.current.has(selectedVideoId)) return

			try {
				video.currentTime = viewProgress.videoProgress
				restoredProgressForMediaRef.current.add(selectedVideoId)
			} catch {}
		},
		[postId, selectedVideoId, viewProgress?.videoProgress],
	)

	return { saveProgress, prepareVideoSwitch, tryRestoreOnMetadata, viewProgress }
}
